import React, { useMemo, useState } from "react";
import { Select, Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { fetchSafetyQuestions } from "../../services/safetyAPI";
import { filterOption } from "../../utils/textUtils";
import { useAuth } from "../../hooks/useAuth";
import CreateSafetyCategoryModal from "./CreateSafetyCategoryModal";

const renderCreateCategoryFooter = (menu, onCreateClick) => (
  <>
    {menu}
    <div
      style={{
        borderTop: "1px solid #f0f0f0",
        padding: "8px",
      }}
    >
      <Button
        type="link"
        icon={<PlusOutlined />}
        onMouseDown={(event) => event.preventDefault()}
        onClick={onCreateClick}
        style={{ padding: 0, width: "100%", textAlign: "left" }}
      >
        Create Category
      </Button>
    </div>
  </>
);

const SafetyCategorySelect = ({
  value,
  onChange,
  placeholder = "Select category",
  disabled = false,
  className,
  allowClear = true,
  allowCreate = true,
  ...rest
}) => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [extraCategories, setExtraCategories] = useState([]);

  const { data } = useQuery({
    queryKey: ["safetyQuestions", projectId],
    queryFn: () => fetchSafetyQuestions(projectId),
    enabled: !!projectId,
  });

  const categories = useMemo(() => {
    const byKey = new Map();

    for (const question of data?.questions || []) {
      const raw = (question.category || "").trim();
      if (!raw) continue;
      const key = raw.toLowerCase();
      if (!byKey.has(key)) {
        byKey.set(key, raw);
      }
    }

    for (const category of extraCategories) {
      const raw = (category || "").trim();
      if (!raw) continue;
      const key = raw.toLowerCase();
      if (!byKey.has(key)) {
        byKey.set(key, raw);
      }
    }

    if (value) {
      const raw = String(value).trim();
      if (raw) {
        const key = raw.toLowerCase();
        if (!byKey.has(key)) {
          byKey.set(key, raw);
        }
      }
    }

    return Array.from(byKey.values()).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" })
    );
  }, [data?.questions, extraCategories, value]);

  const handleCategoryCreated = (category) => {
    const trimmed = (category || "").trim();
    if (!trimmed) return;

    setExtraCategories((prev) => {
      const exists = prev.some(
        (item) => item.toLowerCase() === trimmed.toLowerCase()
      );
      if (exists) return prev;
      const alreadyKnown = categories.some(
        (item) => item.toLowerCase() === trimmed.toLowerCase()
      );
      if (alreadyKnown) return prev;
      return [...prev, trimmed];
    });

    const matched =
      categories.find((item) => item.toLowerCase() === trimmed.toLowerCase()) ||
      trimmed;
    onChange?.(matched);
  };

  const dropdownRender = allowCreate
    ? (menu) =>
        renderCreateCategoryFooter(menu, () => setShowCreateModal(true))
    : undefined;

  return (
    <>
      <Select
        showSearch
        optionFilterProp="children"
        filterOption={filterOption}
        value={value || undefined}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        className={className}
        allowClear={allowClear}
        getPopupContainer={() => document.body}
        options={categories.map((category) => ({
          value: category,
          label: category,
        }))}
        dropdownRender={dropdownRender}
        {...rest}
      />
      <CreateSafetyCategoryModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCategoryCreated={handleCategoryCreated}
        existingCategories={categories}
      />
    </>
  );
};

export default SafetyCategorySelect;
